/**
 * DEFAULTS — every tunable param in one place (CONTRACT §3 / D12 / Q-DIAL / Q-LATENCY).
 * NOTHING timing-related is hard-coded outside this object; options passed to
 * createVoyagePreview / run() shallow-override these.
 *
 * NOTE (C2): NO frame LAYOUT numbers live here. All margins, offsets, fonts,
 * colors, line widths come from the server `frame` object (base_config). The
 * palette below is only for the embed-OWNED animation chrome (route trace, ship
 * dot, skeleton coastline, status checklist, skeleton placeholders) — none of it
 * touches the printed frame's own styling.
 */
import { TCM_STAMP_BLACK } from "./logo.js";

export const DEFAULTS = {
  // mapper-api PUBLIC preview base — the `/api/v3` root (public_api_v3.md §0). All requests are
  // relative to it: `<apiBaseUrl>/voyage/preview`, `<apiBaseUrl>/voyage/preview/{jobId}`,
  // `<apiBaseUrl>/style/assets/<file>`. REQUIRED — no default (Task 4c). Shipping a localhost
  // default in the production bundle meant a storefront that forgot the option silently called the
  // mock server. There is deliberately NO fallback URL: a missing `apiBaseUrl` logs a clear console
  // error and the widget enters its neutral "unavailable" state (see createVoyagePreview).
  apiBaseUrl: undefined,

  // --- choreography timings (all overridable) ------------------------------
  // The reveal plays over a deliberately UNHURRIED ~8s "anticipation" arc:
  //   fetchMinMs (2s data) → traceMs (route + map) → mapReadyHoldMs (beat) → morph,
  // i.e. 2s of fetch then the animation stretched over the remaining ~6s.
  traceMs: 4600, // nominal route-trace duration; CLAMPED to [traceFloorMs, traceCeilMs]
  traceFloorMs: 2000, // floor — never reveal before this elapses (subsumed by the staged arc)
  traceCeilMs: 9000, // ceiling (raised to fit the longer trace)
  skeletonFadeAt: 0.6, // coastline fades in partway through the trace (D12) → "Building your map"
  skeletonFadeMs: 1200, // crossfade-in duration for the skeleton (slower, calmer)
  easeFinalFrom: 0.88, // route eases/slows over the final ~12%
  revealMs: 500, // snap-reveal crossfade of the PNG (300–500ms)
  pollMs: 1500, // job poll interval
  // Poll retry cap (Task 2). The poll loop swallows transient GET errors and re-polls; without a
  // cap, a render service that is down or has LOST the job (never returns ready/failed) spins the
  // customer's checklist forever. We cap the number of CONSECUTIVE failed poll GETs; once exhausted
  // the widget fails the SAME way a `status: "failed"` poll does — it freezes in place and reports
  // { outcome:"terminal", reason:"render-failed" } via the API (no copy). A count (not a time budget) is used
  // because the poll is a fixed-interval loop (pollMs) — count × pollMs is the effective time budget
  // (10 × 1500ms ≈ 15s), and a count reads directly off the existing retry structure. A successful
  // poll that returns a non-terminal status resets the counter (only a sustained outage trips it).
  pollMaxRetries: 10,

  // --- anticipation staging (the floating stage checklist) -----------------
  // A minimum 2s "Fetching your data" beat plays BEFORE anything maps-related shows
  // (the API is fast, but the labour-illusion beat makes the AI data-fetch read as
  // real work). Then a beat is held on "Map ready" after the route finishes, before
  // the high-res map morphs in. Total per load ≈ fetchMinMs + traceMs + mapReadyHoldMs ≈ 8s.
  fetchMinMs: 2000, // minimum visible "Fetching your data" stage (gates the route start)
  mapReadyHoldMs: 1400, // hold on "Map ready" after the trace ends, before the morph
  checklist: true, // master switch for the floating stage-checklist card
  checklistLogoUrl: TCM_STAMP_BLACK, // brand mark that rides the ACTIVE stage row (see logo.js)
  // checklist chrome (embed-owned — NOT the printed frame)
  checklistRowHeight: 26, // px per stage row (drives how far the logo travels)
  checklistOffsetX: -16, // px: nudge the card up/left so it bleeds onto the inset border (floats)
  checklistOffsetY: -16,
  checklistCardBg: "rgba(255,255,255,0.94)",
  checklistShadow: "0 8px 26px rgba(32,60,81,0.22)", // soft drop-shadow → hovering feel
  checklistTextColor: "#203C51", // active stage label
  checklistMutedColor: "#94A2AD", // pending / completed stage label
  checklistTickColor: "#3E8E69", // completed-stage tick
  checklistPulseColor: "#6CA6DA", // soft blue glow that pulses behind the active-stage logo
  // staged checklist wording (one row each; the 4 anticipation stages)
  copyStageFetch: "Fetching your data",
  copyStageRoute: "Building your route",
  copyStageMap: "Building your map",
  copyStageReady: "Map ready",

  // (Failure copy removed 2026-07-03 — the widget paints no failure messages. On failure it freezes
  // in place and reports { outcome, reason } via the event/return API; the storefront owns all
  // failure UX. See preview.js failFrozen.)

  // --- animation chrome palette (embed-owned, NOT the printed frame) -------
  routeColor: "#203C51", // the route trace
  insetBgColor: "#EDE7DF", // empty/loading inset backdrop (slightly darker than page)
  accentColor: "#B7835A", // ship-dot / accents
  skeletonColor: "#203C51", // coastline strokes (tinted down via opacity)
  skeletonOpacity: 0.28, // thin, hand-drawn-ish feel
  routeWidth: 3, // in inset-pixel-space units (scaled by the viewBox)
  skeletonWidth: 1, // in inset-pixel-space units

  // --- progressive port markers (FEEDBACK item 2) --------------------------
  // As the route traces, drop each port marker (start → ports → end) the moment the
  // trace head reaches it, and dip the trace speed momentarily at each one for an
  // A→B→C→D beat. Markers match the rendered PNG's port circles so the morph is seamless.
  portMarkers: true, // master switch for the progressive drop
  portMarkerRadius: 4.5, // inset-px; matches renderStyle pointRadiusPx
  portMarkerColor: "#B7835A", // matches renderStyle circleColor
  portMarkerStrokeColor: "#203C51", // matches renderStyle circleStrokeColor
  portMarkerStrokeWidth: 1.5, // matches renderStyle circleStrokeWidthPx
  portMarkerRevealMs: 360, // pop-in duration per marker (scale+fade, slight overshoot)
  portEase: true, // dip the trace speed near each port
  portEaseDepth: 0.6, // 0..1 — how much speed drops at a port (1 = momentary stop)
  portEaseWidth: 0.02, // half-width of the dip, in drawn-fraction units
  portEaseMinSpeed: 0.28, // floor so the head never fully stalls (esp. many ports)

  // --- frame-field skeleton placeholders (C6) ------------------------------
  // Light-grey rounded rects shown before a field value arrives. Sizes are in
  // PRINT-pixel space (drawn in the frame SVG viewBox), so they scale with the
  // whole product. Width is a fraction of the available text column.
  placeholderColor: "#E2DCD3", // light-grey skeleton fill
  placeholderRadiusPx: 18, // rounded-rect corner radius (print-px)
  placeholderShimmer: false, // no pulse — the blank skeleton stays static (owner: the pulse read as a gimmick)
  // FEEDBACK item 7 — placeholder bar sizing. Title is a fraction of the text COLUMN. This SAME
  // constant also drives the real title's wrap width (preview.js #drawMapTitle) so the skeleton
  // and the eventual text share one footprint — change it here and both move together. The
  // right-hand stack (ship name + 3 detail rows) is sized as a fraction of the PAGE width: ship
  // name is the widest/tallest, the 3 details thinner + shorter.
  // 0.30 (was 0.50): at 50% the title bar reached the page centre, where every centred partner
  // logo (cunard crest, P&O pando) sits — collision. 30% clears them; the logo-aware clamp below
  // tightens further per-style so this stays responsive if a logo/margin moves.
  placeholderTitleWidthFrac: 0.3, // of text column — drives BOTH skeleton bar and real title wrap
  placeholderLogoGapFracPage: 0.02, // min gap (frac of page W) a title/ship bar keeps from a logo
  placeholderShipWidthFracPage: 0.15, // ship name — widest of the right stack
  placeholderDatesWidthFracPage: 0.12,
  placeholderDurationWidthFracPage: 0.08,
  placeholderMileageWidthFracPage: 0.1,
  // bar height as a fraction of the field's font-size — ship/title bigger, details shorter.
  placeholderHeightFrac: 0.78, // title + ship name
  placeholderDetailHeightFrac: 0.6, // dates / duration / mileage (slightly smaller)

  // --- field-text streaming (build-phase left→right write) -----------------
  // During the build animation the ship name + 3 detail rows "write themselves in" one after
  // another via a left→right clip wipe (the title is excluded — it binds live to user input).
  // Kicks off at the "Building your map" beat (skeletonFadeAt) and resolves at the morph reveal.
  // Honours prefers-reduced-motion (snaps, no wipe). The title is never streamed.
  streamFieldMs: 360, // duration of each field's L→R write
  streamGapMs: 140, // beat between consecutive fields

  // --- partner styling (D14) -----------------------------------------------
  // coBranding ship lifecycle (plan §5/§6): pre-reveal, the ship slot is a plain GREY BOX
  // at ~the ship-slot size (owner decision 2026-06-18 — a real silhouette read too close to
  // the final ship). The morph reveal swaps in the style's real ship (e.g. Arcadia). The
  // coBranding logos/ship are carried in the API-supplied `frame.layout.coBranding` (D14).

  // --- request defaults (public_api_v3.md §1/§3) ---------------------------
  // The same identifiers Shopify already holds. A run({ style, height, width, layout, ... }) call
  // overrides any of these for that voyage; orientation is an INPUT (`layout`), never a toggle.
  style: "Standard", // raw Shopify `_style` slug; "none"/missing → "Standard" (server-normalised)
  height: 40, // print height (same number Shopify parses from the variant title)
  width: 30, // print width
  layout: "Portrait", // "Portrait" | "Landscape" — the customer's orientation choice (passed in)

  // --- preloaded frame recipes (D15 / public_api_v3.md §1d/§3) --------------
  // The product's raw Shopify variant-title strings. On mount the widget fetches every one's
  // frame recipe from GET /api/v3/prints/frames ONCE (keyed by the raw string), so setFrame()
  // switches size/style/layout instantly with no per-switch network. The widget parses none of
  // these — it forwards them verbatim and the server resolves (D14).
  variants: [], // e.g. ["30 x 40 cm / Portrait", "30 x 40 cm / Landscape", "50 x 70 cm / Portrait"]
  // Whole-call failure of /prints/frames (§1d): the widget emits `rendererror { stage:"frames" }` and
  // retries; it paints nothing and NEVER falls back to the bundled Standard default (wrong shell).
  framesRetryMs: 4000, // backoff between /prints/frames retries on a whole-call failure
  framesMaxRetries: 4, // give up (emit rendererror) after this many failed attempts

  // Coastline skeleton master switch (FEEDBACK follow-up). Set `coastline: false` at mount to
  // skip it: the embed tells the server NOT to build it (no payload arrives) and the trace goes
  // straight route → morph with no "Building your map" skeleton phase. Flick on/off freely;
  // nothing else to unwind.
  coastline: true,
};
